import { fuzzyScore } from "@hq/core";
import type { ComponentType } from "react";

/**
 * The action registry is the single place an action is defined. Feature packages and the app
 * register into it; the palette lists it, the shortcut handler dispatches from it, and tooltips
 * read shortcuts from it, so they can never disagree. Pure TypeScript: no React at runtime.
 */

export type ActionContext = {
  focusedPaneType?: string;
  selectedTaskId?: string;
  currentNoteId?: string;
  route?: string;
  [k: string]: unknown;
};

export type Action = {
  /** Dotted, stable: `"notes.new"`, `"tasks.new"`, `"go.today"`. */
  id: string;
  /** Shown in the palette: `"New note"`. */
  title: string;
  /** Palette heading: `"Notes"`, `"Tasks"`, `"Navigate"`, `"View"`. */
  group?: string;
  /** Extra search terms. */
  keywords?: string[];
  /**
   * `"mod+n"`, `"mod+shift+p"`, a single key like `"c"`, or a space-separated sequence like
   * `"g t"`. Single-key shortcuts fire only when no editable element has focus.
   */
  shortcut?: string;
  icon?: ComponentType<{ className?: string }>;
  /** Context predicate; the action is listed and dispatchable only when it returns true. Absent = always. */
  when?: (ctx: ActionContext) => boolean;
  run: (ctx: ActionContext) => void | Promise<void>;
  /**
   * Whether a modifier shortcut (`mod+…`) may fire while an editable element (input, textarea,
   * contenteditable, CodeMirror) has focus. Default true. Shortcuts without a modifier never
   * fire inside editable elements regardless of this flag.
   */
  allowInEditor?: boolean;
  /** When several eligible actions share a shortcut the highest priority wins. Default 0. */
  priority?: number;
};

export type RegistrySnapshot = {
  /** Every registered action, in registration order. Not filtered by `when`. */
  actions: readonly Action[];
  /** Most recently run first; only ids that are still registered. */
  recent: readonly string[];
};

export interface ActionRegistry {
  /** Returns an unregister function. Registering an id that exists replaces the action. */
  register(action: Action): () => void;
  get(id: string): Action | undefined;
  /** Actions whose `when` accepts `ctx` (all of them when `ctx` is omitted), in registration order. */
  list(ctx?: ActionContext): Action[];
  subscribe(listener: () => void): () => void;
  /** Runs an action by id, records it as recently used. Rejects for an unknown id; a `when` that refuses `ctx` is a no-op. */
  run(id: string, ctx: ActionContext): Promise<void>;
  /** Moves the id to the front of the recently-used list. */
  markUsed(id: string): void;
  /** Recently used ids, most recent first. */
  recent(): readonly string[];
  /** Referentially stable until something changes; for `useSyncExternalStore`. */
  snapshot(): RegistrySnapshot;
}

export const MAX_RECENT = 8;

export function createActionRegistry(): ActionRegistry {
  const actions = new Map<string, Action>();
  const listeners = new Set<() => void>();
  let recent: string[] = [];
  let cached: RegistrySnapshot | null = null;

  const notify = (): void => {
    cached = null;
    for (const listener of [...listeners]) listener();
  };

  const recentRegistered = (): string[] => recent.filter((id) => actions.has(id));

  const markUsed = (id: string): void => {
    if (!actions.has(id)) return;
    recent = [id, ...recent.filter((r) => r !== id)].slice(0, MAX_RECENT);
    notify();
  };

  return {
    register(action) {
      actions.set(action.id, action);
      notify();
      return () => {
        // A stale unregister (after the id was re-registered) must not remove the replacement.
        if (actions.get(action.id) !== action) return;
        actions.delete(action.id);
        notify();
      };
    },
    get: (id) => actions.get(id),
    list(ctx) {
      const all = [...actions.values()];
      if (ctx === undefined) return all;
      return all.filter((a) => a.when === undefined || a.when(ctx));
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    async run(id, ctx) {
      const action = actions.get(id);
      if (action === undefined) throw new Error(`Unknown action "${id}"`);
      if (action.when !== undefined && !action.when(ctx)) return;
      markUsed(id);
      await action.run(ctx);
    },
    markUsed,
    recent: recentRegistered,
    snapshot() {
      cached ??= { actions: [...actions.values()], recent: recentRegistered() };
      return cached;
    },
  };
}

export type ActionMatch = { action: Action; score: number };

const KEYWORD_WEIGHT = 0.8;
const GROUP_WEIGHT = 0.5;

/** Best fuzzy score over title, keywords, and group; -1 when nothing matches. */
export function scoreAction(action: Action, query: string): number {
  let best = fuzzyScore(query, action.title);
  for (const keyword of action.keywords ?? []) {
    const s = fuzzyScore(query, keyword);
    if (s >= 0) best = Math.max(best, s * KEYWORD_WEIGHT);
  }
  if (action.group !== undefined) {
    const s = fuzzyScore(query, action.group);
    if (s >= 0) best = Math.max(best, s * GROUP_WEIGHT);
  }
  return best;
}

/**
 * Ranks actions for a palette query. A blank query lists `recent` ids first (most recent
 * first), then the rest grouped by `group` in order of first appearance, titles sorted within
 * each group. Otherwise every action with a positive fuzzy score, best first, ties by title.
 */
export function matchActions(
  actions: readonly Action[],
  query: string,
  recent: readonly string[] = [],
): ActionMatch[] {
  const q = query.trim();
  if (q === "") {
    const byId = new Map(actions.map((a) => [a.id, a]));
    const head: ActionMatch[] = [];
    for (const id of recent) {
      const action = byId.get(id);
      if (action !== undefined && !head.some((m) => m.action === action)) {
        head.push({ action, score: 0 });
      }
    }
    const used = new Set(head.map((m) => m.action));
    const groupOrder = new Map<string, number>();
    const rest = actions.filter((a) => !used.has(a));
    for (const a of rest) {
      const g = a.group ?? "";
      if (!groupOrder.has(g)) groupOrder.set(g, groupOrder.size);
    }
    rest.sort(
      (a, b) =>
        (groupOrder.get(a.group ?? "") ?? 0) - (groupOrder.get(b.group ?? "") ?? 0) ||
        a.title.localeCompare(b.title),
    );
    return [...head, ...rest.map((action) => ({ action, score: 0 }))];
  }
  const matches: ActionMatch[] = [];
  for (const action of actions) {
    const score = scoreAction(action, q);
    if (score > 0) matches.push({ action, score });
  }
  matches.sort((a, b) => b.score - a.score || a.action.title.localeCompare(b.action.title));
  return matches;
}
